import { useState } from "react";
import { useDataStore } from "@store/user_org";
import api from "@services/api/client";

type LogEntry = { time: string; msg: string; type: "info" | "success" | "error" };

function now() {
  return new Date().toLocaleTimeString();
}

export default function LoadTestScreen() {
  const orgData = useDataStore((s) => s.orgData);
  const [counts, setCounts] = useState({ ordres: 100, dossiers: 100, appointments: 200, jobs: 200 });
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [running, setRunning] = useState(false);

  const log = (msg: string, type: LogEntry["type"] = "info") => {
    setLogs((prev) => [...prev, { time: now(), msg, type }]);
  };

  const orgId = orgData?.id;

  const handleCreate = async () => {
    if (!orgId) return;
    setRunning(true);
    log("Envoi de la requête de création...");

    try {
      const { data } = await api.post("/loadtest/seed", {
        org_id: orgId,
        ordres: counts.ordres,
        dossiers: counts.dossiers,
        appointments: counts.appointments,
        jobs: counts.jobs,
      });

      log(`${data.dossiers.length} dossiers créés`, "success");
      log(`${data.ordres.length} ordres créés`, "success");
      log(`${data.appointments.length} rendez-vous créés`, "success");
      log(`${data.jobs.length} jobs créés`, "success");
      const total = data.dossiers.length + data.ordres.length + data.appointments.length + data.jobs.length;
      log(`Terminé ! ${total} documents créés en ${(data.elapsed_ms / 1000).toFixed(1)}s`, "success");
    } catch (e: any) {
      log(`Erreur: ${e.message || e}`, "error");
    }

    setRunning(false);
  };

  const handleScan = async () => {
    if (!orgId) return;
    setRunning(true);
    log("[DRY RUN] Scan des documents LOADTEST...");

    try {
      const { data } = await api.post("/loadtest/scan", { org_id: orgId });
      log(`[DRY RUN] Ordres: ${data.ordres}`, data.ordres > 0 ? "info" : "success");
      log(`[DRY RUN] Dossiers: ${data.dossiers}`, data.dossiers > 0 ? "info" : "success");
      log(`[DRY RUN] RDV: ${data.appointments}`, data.appointments > 0 ? "info" : "success");
      log(`[DRY RUN] Jobs: ${data.jobs}`, data.jobs > 0 ? "info" : "success");
      log(`[DRY RUN] Total: ${data.total} documents LOADTEST trouvés`, data.total === 0 ? "success" : "info");
    } catch (e: any) {
      log(`Erreur: ${e.message || e}`, "error");
    }

    setRunning(false);
  };

  const handleCleanup = async () => {
    if (!orgId) return;
    setRunning(true);
    log("Suppression des documents LOADTEST...");

    try {
      const { data } = await api.post("/loadtest/cleanup", { org_id: orgId });
      log(`${data.ordres} ordres supprimés`, "success");
      log(`${data.dossiers} dossiers supprimés`, "success");
      log(`${data.appointments} RDV supprimés`, "success");
      log(`${data.jobs} jobs supprimés`, "success");
      log(`Terminé ! ${data.total} documents supprimés en ${(data.elapsed_ms / 1000).toFixed(1)}s`, "success");
    } catch (e: any) {
      log(`Erreur: ${e.message || e}`, "error");
    }

    setRunning(false);
  };

  return (
    <div className="h-screen flex flex-col overflow-auto p-10 max-w-800">
      <h1 className="text-2xl font-bold mb-2">Load Test</h1>
      <p className="text-neutral-500 mb-6">
        Org: <span className="font-mono text-neutral-700">{orgId || "non connecté"}</span>
      </p>

      {/* Counts */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {(["ordres", "dossiers", "appointments", "jobs"] as const).map((key) => (
          <div key={key} className="flex items-center gap-2">
            <label className="w-32 text-sm font-medium capitalize">{key}</label>
            <input
              type="number"
              className="border rounded px-3 py-1.5 w-24 text-sm"
              value={counts[key]}
              onChange={(e) => setCounts((prev) => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
              disabled={running}
            />
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-4">
        <button
          onClick={handleCreate}
          disabled={running || !orgId}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50 hover:bg-blue-700 transition-colors"
        >
          Créer les documents
        </button>
        <button
          onClick={handleScan}
          disabled={running || !orgId}
          className="px-5 py-2 bg-neutral-600 text-white rounded-lg font-medium disabled:opacity-50 hover:bg-neutral-700 transition-colors"
        >
          Dry run (scanner)
        </button>
        <button
          onClick={handleCleanup}
          disabled={running || !orgId}
          className="px-5 py-2 bg-red-600 text-white rounded-lg font-medium disabled:opacity-50 hover:bg-red-700 transition-colors"
        >
          Supprimer tous les LOADTEST
        </button>
      </div>

      {/* Logs */}
      <div className="flex-1 min-h-0 bg-neutral-50 rounded-lg border p-4 overflow-auto font-mono text-xs">
        {logs.map((l, i) => (
          <div
            key={i}
            className={`py-0.5 ${
              l.type === "error" ? "text-red-600" : l.type === "success" ? "text-green-700" : "text-neutral-600"
            }`}
          >
            <span className="text-neutral-400 mr-2">[{l.time}]</span>
            {l.msg}
          </div>
        ))}
        {logs.length === 0 && <div className="text-neutral-400">En attente...</div>}
      </div>
    </div>
  );
}
